import {ChangeDetectionStrategy, Component, computed, signal} from '@angular/core';
import {AuthService} from "../../services/auth.service";
import {Router, RouterLink} from "@angular/router";
import {finalize} from "rxjs/operators";
import {StateService} from "../../services/state.service";
import {UtilService} from "../../services/util.service";
import {Constants} from "../../util/constants";
import {SessionService} from "../../services/session.service";
import {BusyService} from "../../services/busy.service";
import {NotificationService} from "../../services/notification.service";
import {UserPrefsService} from "../../services/user-prefs.service";
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatInput} from '@angular/material/input';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {form, FormField, pattern, required} from "@angular/forms/signals";
import {FieldErrorsComponent} from "../../controls/field-errors/field-errors.component";
import {PasswordInputComponent} from "../../controls/password-input/password-input.component";

interface LoginModel {
    email: string;
    password: string;
    code: string;
    trustThisDevice: boolean;
}

@Component({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatInput, MatError, MatLabel, MatCheckbox, MatButton, RouterLink, FieldErrorsComponent, FormField, PasswordInputComponent]
})
export class LoginComponent {

    loginModel = signal<LoginModel>({
        email: '',
        password: '',
        code: '',
        trustThisDevice: false
    });

    loginForm = form(this.loginModel, (schemaPath) => {
        required(schemaPath.email, {message: Constants.ERR_REQUIRED});
        pattern(schemaPath.email, Constants.EMAIL_REGEX, {message: Constants.ERR_INVALID_EMAIL});
        required(schemaPath.password, {message: Constants.ERR_REQUIRED});
        required(schemaPath.code, {message: Constants.ERR_REQUIRED});
        pattern(schemaPath.code, /\d{6}/, {message: Constants.ERR_INVALID_2FA});
    });

    isTrustedDevice = computed<boolean>(() => {
        const email = this.loginForm.email().value();
        // If we don't have an email ready yet, assume it will be trusted
        if (!email || !this.loginForm.email().valid())
            return true;
        return this.authService.hasTrustToken(email);
    })

    constructor(private router: Router,
                private state: StateService,
                private utilService: UtilService,
                private sessionService: SessionService,
                private busyService: BusyService,
                private notificationService: NotificationService,
                private authService: AuthService,
                private userPrefs: UserPrefsService) {

        if (this.authService.isLoggedIn()) {
            this.authService.logOut().subscribe(() => {
                console.warn("Forced logout");
                this.state.clearAll();
                this.userPrefs.unload();
                this.sessionService.clearSession();
            });
        }
    }

    canSubmit = computed(() => {
        return !this.busyService.isBusy() &&
                this.loginForm.email().valid() &&
                this.loginForm.password().valid() &&
               (this.isTrustedDevice() || this.loginForm.code().valid());
    });

    submit(event: SubmitEvent) {
        event.preventDefault();
        this.busyService.showBusy();
        const email = this.loginForm.email().value();
        const password = this.loginForm.password().value();
        const code = this.loginForm.code().value() || undefined;
        const trustThisDevice: boolean = this.loginForm.trustThisDevice().value();
        const pwa: boolean = window.matchMedia('(display-mode: standalone)').matches;
        this.authService.logIn(email, password, code, trustThisDevice, pwa).pipe(
            finalize(() => {
                this.busyService.showNotBusy();
                this.state.clear('targetUrl');
            })
        ).subscribe({
            next: () => {
                this.state.set('email', email);
                this.userPrefs.load(email);
                const targetUrl = this.state.get('targetUrl')() || '/domains';
                this.router.navigateByUrl(targetUrl).then();
                this.sessionService.restartSession();
                this.notificationService.connect();
            },
            error: this.utilService.getErrorHandler("Login error")
        });
    }
}
